<template>
  <div class="d-flex flex-column">
    <div class="grid-header">
      <h1>{{ $t("tramiteEnergia.titulo") }}</h1>
      <div class="toolbar">
      </div>
    </div>

    <DxDataGrid v-bind="gridProperties" :data-source="gridSource" ref="grid" class="dx-card wide-card grid-tramites">
      <DxToolbar>
        <DxItem location="after" template="exportButtonTemplate" />
        <DxItem v-bind="getResetFiltersGridBtn(() => grid)" />
        <DxItem v-bind="getResetStateGridBtn(() => grid)" />
        <DxItem v-bind="getColumnChooserBtn()" />
      </DxToolbar>
      <DxColumn data-field="FechaAlta" :caption="$t('tramiteEnergia.fechaAlta')" data-type="date" format="dd/MM/yyyy"></DxColumn>
      <DxColumn data-field="CifTitular" :caption="$t('tramiteEnergia.cifTitular')"></DxColumn>
      <DxColumn data-field="DocumentoTitular" :caption="$t('tramiteEnergia.documentoTitular')"></DxColumn>
      <DxColumn data-field="TipoSuministro" :caption="$t('tramiteEnergia.tipoSuministro')">
        <DxLookup column-auto-width="true" v-bind="getLookup('operaciones', 'tipo_suministro')"></DxLookup>
      </DxColumn>
      <DxColumn data-field="TarifaAcceso" :caption="$t('tramiteEnergia.tarifaAcceso')"></DxColumn>
      <DxColumn data-field="Cups" :caption="$t('tramiteEnergia.cups')"></DxColumn>
      <DxColumn data-field="IdExternoTramite" :caption="$t('tramiteEnergia.idExterno')"></DxColumn>
      <DxColumn data-field="CodigoEstado" :caption="$t('tramiteEnergia.estado')" cell-template="estadoTemplate">
        <DxLookup v-bind="getLookup('operaciones', 'estado')"></DxLookup>
      </DxColumn>
      <DxColumn data-field="TieneServiciosAdicionales" :caption="$t('tramiteEnergia.tieneServiciosAdicionales')" cell-template="booleanTemplate">
        <DxLookup v-bind="getColumnLookupBoolean()"></DxLookup>
      </DxColumn>
      <DxColumn data-field="Comision" :caption="$t('tramiteEnergia.comision')" data-type="number">
        <DxFormat v-bind="currencyFormat()"></DxFormat>
      </DxColumn>
      <DxColumn data-field="Retrocomision" :caption="$t('tramiteEnergia.retrocomision')" data-type="number" :calculate-cell-value="formatRetrocomision">
        <DxFormat v-bind="currencyFormat()"></DxFormat>
      </DxColumn>
      <DxColumn data-field="FechaActivacion" :caption="$t('tramiteEnergia.fechaActivacion')" data-type="date" format="dd/MM/yyyy"></DxColumn>
      <DxColumn data-field="FechaBaja" :caption="$t('tramiteEnergia.fechaBaja')" data-type="date" format="dd/MM/yyyy"></DxColumn>
      <DxColumn data-field="FechaModificacion" :caption="$t('tramiteEnergia.fechaUltModificacion')" data-type="date" format="dd/MM/yyyy"></DxColumn>
      <DxColumn data-field="NombreUsuarioUltimaModificacion" :caption="$t('tramiteEnergia.nombreUsuarioUltimaModificacion')" />
      <DxColumn data-field="CodigoEstadoLiquidacion" :caption="$t('operaciones.grid.liquidado')" cell-template="liquidadoTemplate">
        <DxLookup v-bind="getLookup('operaciones', 'liquidado')"></DxLookup>
      </DxColumn>
      <DxColumn type="buttons" :caption="$t('tramites.grid.acciones')" :fixed="true" fixed-position="right" width="100px" :buttons="[
        {
          cssClass: 'btn btn-link text-decoration-none btn-sm',
          text: $t('operaciones.grid.ir_a_chat'),
          icon: 'fa-regular fa-comment-dots text-success',
          onClick: (e: any) => irAChat(e)
        },
        {
          cssClass: 'btn btn-link text-decoration-none btn-sm',
          text: $t('operaciones.grid.ver_detalle'),
          icon: 'fa-solid fa-magnifying-glass text-success',
          onClick: (e: any) => verDetalle(e)
        }
      ]" />
      <DxColumnFixing :enabled="true" />

      <template #estadoTemplate="{ data }">
        <div :class="[
          `d-inline-block rounded py-1 px-2 `,
          `estado-operacion ${data.value.replace(' ', '')}`,
        ]">
            {{ $t(`operaciones.estado.${data.value}`) }}
        </div>
      </template>
      <template #booleanTemplate="{ data }">
        <BooleanTemplate :value="data.value" />
      </template>
      <template #liquidadoTemplate="{ data }">
        <div :class="[
          `d-inline-block rounded py-1 px-2 `,
          `estado-liquidacion ${data.value.replace(' ', '')}`,
        ]">
            {{ $t(`operaciones.liquidado.${data.value}`) }}
        </div>
      </template>
      <template #exportButtonTemplate>
        <button @click="onExporting" class="btn btn-sm btn-light d-flex align-items-center gap-2 m-2">
          <i class="fas fa-file-excel exportar-excel"></i>
          <span class="text-primary">{{ $t("shared.ui.buttons.exportToExcel") }}</span>
        </button>
      </template>
      <DxMasterDetail :enabled="true" template="masterDetailTemplate" />
      <template #masterDetailTemplate="{ data: tramite }">
        <tramites-grid-detail :template-data="tramite" />
      </template>
    </DxDataGrid>
  </div>
</template>

<script setup lang="ts">
import { useApi } from "@api/integration";
import {
  DxColumn,
  DxDataGrid,
  DxFormat,
  DxLookup,
  DxMasterDetail,
  DxColumnFixing,
  DxItem,
  DxToolbar,
} from "devextreme-vue/data-grid";
import {
  currencyFormat,
  getLookup,
  getColumnLookupBoolean,
  exportGridToExcel,
  getColumnChooserBtn,
  getResetFiltersGridBtn,
  getResetStateGridBtn,
  getDefaultGridWithStateStoringProperties,
} from "@helpers/grid-helper";
import type CustomStore from "devextreme/data/custom_store";
import { ref, watch } from "vue";
import TramitesGridDetail from "@/components/tramites-energia/TramitesGridDetail.vue";
import type DevExpress from "devextreme";
import { type DXLoadOptions } from "@/.shared/.api";
import BooleanTemplate from "@/.shared/components/BooleanTemplate.vue";
import { TipoOperacionEnum } from "@/.shared/enums/TipoOperacionEnum";
import { useRouter } from "@/.shared/services/routing-services";

const props = defineProps({
  idOperacion: {
    type: String,
    required: false,
  },
});

const router = useRouter();
const api = useApi();
const gridSource = ref<CustomStore>();
const grid = ref<DxDataGrid>();
const gridProperties = getDefaultGridWithStateStoringProperties("tramites-energia-operacion-grid-state.v4", () => grid.value);

watch(() => props.idOperacion, (newIdOperacion) => {
  if (newIdOperacion) {
    gridSource.value = api.apiStore({
      key: "IdTramite",
      load: async (p) => {
        const q = p as DXLoadOptions;
        if (q.filter != null && q.filter.length >= 0)
          q.filter = [["IdOperacion", "=", newIdOperacion], "and", q.filter];
        else
          q.filter = ["IdOperacion", "=", newIdOperacion];
        return await api.callStandardApi("getTramitesEnergiaForGrid", { showLoading: false }, q);
      },
    });
  }
});

async function verDetalle(data: DevExpress.ui.dxDataGrid.ColumnButtonClickEvent) {
  await router.push({ name: "tramite", query: { idOperacion: data.row?.data.IdOperacion, tipoOperacion: TipoOperacionEnum.Energia, idTramite: data.row?.data.IdTramite, tipoTramite: data.row?.data.TipoSuministro } });
}

async function irAChat(data: DevExpress.ui.dxDataGrid.ColumnButtonClickEvent) {
  const rutaBO = import.meta.env.VITE_BASE_URL;
  const url = `${rutaBO}/comunicaciones-operacion/${data.row?.data.IdOperacion}/${data.row?.data.CodigoSecuencial.toString().padStart(10, '0')}/${TipoOperacionEnum.Energia}`;
  window.open(url, '_blank');
}

const formatRetrocomision = (rowData: any) => {
  return rowData.Retrocomision ? -Math.abs(rowData.Retrocomision) : rowData.Retrocomision;
};

const onExporting = async () => {
  await exportGridToExcel(
    grid.value!.instance,
    'Tramites.xlsx',
    'Tramites',
    true
  );
};

defineExpose({
  refreshGrid: () => {
    grid.value?.instance?.refresh();
  }
});

</script>

<style lang="scss" scoped>


.lupa,
.chat,
.exportar-excel {
  color: var(--text-success-default);
}

.detalle {
  color: var(--text-primary-default);
  cursor: pointer;
}
</style>
<style>
.grid-tramites .dx-datagrid {
  max-height: calc(100vh - 250px);
}
</style>
